import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sortDragpinNames } from "./hooks/dragpinAnswer";

const themeColors = {
  primary: "#3C6F7D",
  secondary: "#5F6D7E",
  success: "#0CA49D",
  warning: "#FFA319",
  error: "#FF1943",
  info: "#97CFC0",
  black: "#101828",
  white: "#ffffff",
  stroke: "#D0D5DD",
  primaryAlt: "#000C57",
};

// ✅ PENTING: biar gak bikin array baru setiap render saat props persons tidak dikirim
export const DEFAULT_PERSONS = [
  { name: "Ika" },
  { name: "Hafidh" },
  { name: "Fourin" },
  { name: "Septi" },
  { name: "Yahya" },
];

const DEFAULT_IMAGES = {
  floor: "/floor.jpg",
  balcony: "/balkon.jpg",
  dining: "/dining.jpg",
  meeting: "/meeting.png",
  conference: "/conference.jpg",
  desk: "/desk.jpg",
};

export default function DragPinRooms({
  // items,
  dispatch,
  questionState,
  isExplanation = false,
  presetRoomCount = 5,
  presetPinCount,
  persons = DEFAULT_PERSONS,
  presetPinCustomNames, // ✅ custom pin names
  presetRoomCustomNames, // ✅ custom room names
  presetExplanation,
  keyAnswerText, // ✅ kunci jawaban untuk cek
  dragpinPins, // ✅ pin metadata [{name, label}] untuk tampilkan deskripsi
}) {
  const images = DEFAULT_IMAGES;

  // ✅ Media query hook replacement (plain JS)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  const [isTablet, setIsTablet] = useState(() => window.innerWidth < 960);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
      setIsTablet(window.innerWidth < 960);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isDownSm = isMobile;
  const isDownMd = isTablet;

  // ✅ Helper: map person name ke custom display name, fallback ke "Pin X"
  const getDisplayNameForPin = (personIndex) => {
    if (presetPinCustomNames && presetPinCustomNames[personIndex]) {
      return presetPinCustomNames[personIndex];
    }
    return `Pin ${personIndex + 1}`;
  };

  // ✅ Helper: map room index ke custom display name, fallback ke "Room X"
  const getDisplayNameForRoom = (roomIndex) => {
    if (presetRoomCustomNames && presetRoomCustomNames[roomIndex]) {
      return presetRoomCustomNames[roomIndex];
    }
    return `Room ${roomIndex + 1}`;
  };

  const BASE_W = 760;
  const BASE_H = useMemo(() => (isDownSm ? 520 : 400), [isDownSm]);

  const clamp = useCallback((v, min, max) => Math.max(min, Math.min(max, v)), []);

  const clampRooms = (n) => Math.max(1, Math.min(100, Math.floor(n || 1))); // ✅ max 100 rooms
  const roomCount = clampRooms(presetRoomCount);

  // ✅ Warna untuk setiap room (cycling jika > 6 rooms)
  const ROOM_COLORS = ["#99daff", "#bfecac", "#ffd699", "#ff9999", "#ffeb99", "#cea8f0"];
  const getRoomColor = (roomIdx) => ROOM_COLORS[roomIdx % ROOM_COLORS.length];

  const ROOM_CONFIGS = useMemo(() => {
    // ✅ Helper: generate rooms dengan layout simetris berdasarkan jumlah room
    const generateRoomConfigs = (count) => {
      const configs = [];

      if (isDownSm) {
        // ✅ MOBILE layout
        const pad = 16;
        const gap = 14;

        // Tentukan layout: untuk N rooms, bagi seimbang antara atas dan bawah
        let topCount = Math.ceil(count / 2);
        let bottomCount = count - topCount;

        // Tapi jika jumlah ganjil, bisa atur beda
        if (count === 1) { topCount = 1; bottomCount = 0; }
        else if (count === 2) { topCount = 1; bottomCount = 1; }
        else if (count === 3) { topCount = 2; bottomCount = 1; } // ✅ 3 rooms berjajar horizontal

        // ✅ Kalau hanya 1 room, full height; kalau > 1, fixed height
        const topH = count === 1 ? (BASE_H - pad * 2) : 220;
        const topW = (BASE_W - pad * 2 - gap * (topCount - 1)) / topCount;

        // Generate top row
        for (let i = 0; i < topCount; i++) {
          configs.push({
            code: `Room ${i + 1}`,
            label: `Room ${i + 1}`,
            x: pad + i * (topW + gap),
            y: pad,
            w: topW,
            h: topH,
            bgKey: undefined,
          });
        }

        // Generate bottom row
        if (bottomCount > 0) {
          const bottomY = pad + topH + gap;
          const bottomH = BASE_H - bottomY - pad;
          const bottomW = (BASE_W - pad * 2 - gap * (bottomCount - 1)) / bottomCount;

          for (let i = 0; i < bottomCount; i++) {
            configs.push({
              code: `Room ${topCount + i + 1}`,
              label: `Room ${topCount + i + 1}`,
              x: pad + i * (bottomW + gap),
              y: bottomY,
              w: bottomW,
              h: bottomH,
              bgKey: undefined,
            });
          }
        }

        return configs;
      } else {
        // ✅ DESKTOP/TABLET layout
        const pad = 18;
        const gap = 15;

        // Layout untuk desktop lebih flexible
        let topCount = Math.ceil(count / 2);
        let bottomCount = count - topCount;

        if (count === 1) { topCount = 1; bottomCount = 0; }
        else if (count === 2) { topCount = 1; bottomCount = 1; }
        else if (count === 3) { topCount = 2; bottomCount = 1; }
        else if (count === 4) { topCount = 2; bottomCount = 2; }
        else if (count === 5) { topCount = 3; bottomCount = 2; }
        else if (count === 6) { topCount = 3; bottomCount = 3; }

        const totalH = BASE_H - pad * 2;
        const topH = topCount > 0 && bottomCount > 0 ? totalH * 0.48 : totalH;
        const bottomH = bottomCount > 0 ? totalH * 0.48 : 0;

        // Generate top row
        const topW = (BASE_W - pad * 2 - gap * (topCount - 1)) / topCount;
        for (let i = 0; i < topCount; i++) {
          configs.push({
            code: `Room ${i + 1}`,
            label: `Room ${i + 1}`,
            x: pad + i * (topW + gap),
            y: pad,
            w: topW,
            h: topH,
            bgKey: undefined,
          });
        }

        // Generate bottom row
        if (bottomCount > 0) {
          const bottomY = pad + topH + gap;
          const bottomW = (BASE_W - pad * 2 - gap * (bottomCount - 1)) / bottomCount;

          for (let i = 0; i < bottomCount; i++) {
            configs.push({
              code: `Room ${topCount + i + 1}`,
              label: `Room ${topCount + i + 1}`,
              x: pad + i * (bottomW + gap),
              y: bottomY,
              w: bottomW,
              h: bottomH,
              bgKey: undefined,
            });
          }
        }

        return configs;
      }
    };

    return generateRoomConfigs(roomCount);
  }, [BASE_H, isDownSm, roomCount]);
  const activeRooms = useMemo(() => ROOM_CONFIGS.slice(0, roomCount), [ROOM_CONFIGS, roomCount]);
  const activeCodes = useMemo(() => activeRooms.map((r) => r.code), [activeRooms]);

  const clampPins = (n, max) => Math.max(1, Math.min(max, Math.floor(n || 1)));

  // ✅ Pin count: prioritaskan presetPinCustomNames length (data dari backend),
  // lalu presetPinCount, baru fallback ke persons.length
  const pinCount = useMemo(() => {
    const customLen = presetPinCustomNames?.length;
    const max = customLen || persons.length || 1;
    const raw = customLen || presetPinCount || persons.length || 1;
    return clampPins(raw, max);
  }, [presetPinCount, presetPinCustomNames, persons.length]);

  // ✅ Generate persons dari custom names kalau ada, fallback ke default
  const activePersons = useMemo(() => {
    if (presetPinCustomNames && presetPinCustomNames.length > 0) {
      return presetPinCustomNames.slice(0, pinCount).map((name) => ({ name }));
    }
    return persons.slice(0, pinCount);
  }, [presetPinCustomNames, persons, pinCount]);
  const activePersonsKey = useMemo(() => activePersons.map(p => p.name).join("|"), [activePersons]);
  const activeNameSet = useMemo(() => new Set(activePersons.map((p) => p.name)), [activePersons]);

  // ===== Refs for measuring =====
  const floorRef = useRef(null);
  const poolRef = useRef(null);
  const rootRef = useRef(null);
  const roRafRef = useRef(null);
  const dragNameRef = useRef(null);

  const dragPosRef = useRef({ x: 0, y: 0 });
  const overlayRef = useRef(null);
  const rafMoveRef = useRef(null);

  const dragCandidateRef = useRef(null);

  const DRAG_THRESHOLD = 7; // px  

  const [floorW, setFloorW] = useState(BASE_W);
  const scale = useMemo(() => (floorW > 0 ? floorW / BASE_W : 1), [floorW]);

  const baseTarget = useMemo(() => {
    if (isDownSm) return 46;
    if (isDownMd) return 52;
    return 56;
  }, [isDownMd, isDownSm]);

  const rawPinPx = useMemo(() => baseTarget * scale, [baseTarget, scale]);

  const pinSizePx = useMemo(() => {
    const minPx = isDownSm ? 34 : 40;
    const maxPx = isDownSm ? 54 : 64;
    return clamp(rawPinPx, minPx, maxPx);
  }, [rawPinPx, isDownSm, clamp]);

  const pinBaseEff = useMemo(() => (scale > 0 ? pinSizePx / scale : baseTarget), [pinSizePx, scale, baseTarget]);

  // ===== Placements (interactive only) =====
  const [placements, setPlacements] = useState(() => {
    const m = {};
    activePersons.forEach((p) => (m[p.name] = { zone: "pool" }));
    return m;
  });

  const hasInteractedRef = useRef(false);

  // ===== Drag state =====
  const [draggingName, setDraggingName] = useState(null);
  const [draggingDetectedRoom, setDraggingDetectedRoom] = useState(null);
  const dragFromZoneRef = useRef("pool");
  const dragPointerIdRef = useRef(null);
  const dragOffsetRef = useRef({ ox: 0, oy: 0 });
  const [poolHover, setPoolHover] = useState(false);

  // ===== Helpers =====
  const coerceAnswerText = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v.filter((x) => typeof x === "string");
    if (typeof v === "string") {
      try {
        const parsed = JSON.parse(v);
        if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === "string");
      } catch {
        // ignore
      }
    }
    return [];
  };

  const clampFloorPlacement = useCallback((xBase, yBase) => {
    return {
      xBase: clamp(xBase, 0, BASE_W - pinBaseEff),
      yBase: clamp(yBase, 0, BASE_H - pinBaseEff),
    };
  }, [BASE_W, BASE_H, pinBaseEff, clamp]);

  /**
   * ✅ build posisi pin dari answerText.
   * offsetXBase dipakai supaya "kunci" dan "user" gak numpuk (kiri/kanan).
   */
  const clampInRoom = useCallback((r, x, y, pad) => {
    const minX = r.x + pad;
    const maxX = r.x + r.w - pinBaseEff - pad;
    const minY = r.y + pad;
    const maxY = r.y + r.h - pinBaseEff - pad;

    return {
      xBase: clamp(x, minX, Math.max(minX, maxX)),
      yBase: clamp(y, minY, Math.max(minY, maxY)),
    };
  }, [clamp, pinBaseEff]);

  /**
   * ✅ layout pin di dalam room supaya tidak dempet
   * - gap & padding mengikuti pinBaseEff
   * - cols menyesuaikan lebar room
   */
  const layoutPositionsInRoom = useCallback((r, count) => {
    // padding: jarak dari dinding room
    const pad = Math.max(8, pinBaseEff * (isDownSm ? 0.22 : 0.18));

    // gap minimal antar pin (base coordinate)
    const desiredGap = pinBaseEff * (isDownSm ? 0.75 : 0.9);

    const usableW = Math.max(0, r.w - 2 * pad);
    const usableH = Math.max(0, r.h - 2 * pad);

    // hitung kolom maksimum yang “masuk akal” untuk lebar room
    // (pin + gap) kira-kira butuh (pinBaseEff + desiredGap)
    const maxColsByWidth = Math.max(1, Math.floor((usableW + desiredGap) / (pinBaseEff + desiredGap)));

    // mulai dari sqrt(n) biar seimbang, lalu clamp ke maxColsByWidth
    let cols = Math.min(maxColsByWidth, Math.max(1, Math.ceil(Math.sqrt(count))));
    let rows = Math.max(1, Math.ceil(count / cols));

    // kalau terlalu tinggi (room pendek), coba naikkan cols (biar rows turun) tapi jangan lewat maxColsByWidth
    while (rows > 1 && cols < maxColsByWidth) {
      const nextCols = cols + 1;
      const nextRows = Math.ceil(count / nextCols);
      if (nextRows < rows) {
        cols = nextCols;
        rows = nextRows;
      } else break;
    }

    // step antar pin (gap real) disesuaikan dengan ruang available
    const stepX =
      cols === 1
        ? 0
        : Math.min(desiredGap, (usableW - pinBaseEff) / (cols - 1));

    const stepY =
      rows === 1
        ? 0
        : Math.min(desiredGap, (usableH - pinBaseEff) / (rows - 1));

    // pos start biar grid “centered” di dalam room
    const gridW = pinBaseEff + (cols - 1) * stepX;
    const gridH = pinBaseEff + (rows - 1) * stepY;

    const startX = r.x + pad + (usableW - gridW) / 2;
    const startY = r.y + pad + (usableH - gridH) / 2;

    return { pad, cols, stepX, stepY, startX, startY };
  }, [isDownSm, pinBaseEff]);

  const buildPlacementsFromAnswer = useCallback((incoming, offsetXBase = 0) => {
    const next = {};
    activePersons.forEach((p) => (next[p.name] = { zone: "pool" }));

    // parse "Room 1: Ika,Hafidh" → map["Room 1"] = ["Ika","Hafidh"]
    const map = {};
    incoming.forEach((row) => {
      const [code, rest] = String(row).split(":");
      if (!code) return;
      const names =
        !rest || rest === "null"
          ? []
          : rest.split(",").map((s) => s.trim()).filter(Boolean);
      map[code] = names;
    });

    activeRooms.forEach((r) => {
      const names = map[r.code] || [];
      if (!names.length) return;

      const { pad, cols, stepX, stepY, startX, startY } = layoutPositionsInRoom(r, names.length);

      names.forEach((nm, idx) => {
        if (!activeNameSet.has(nm)) return;

        const col = idx % cols;
        const row = Math.floor(idx / cols);

        const x = startX + col * stepX + offsetXBase;
        const y = startY + row * stepY;

        const clamped = clampInRoom(r, x, y, pad);

        next[nm] = { zone: "floor", xBase: clamped.xBase, yBase: clamped.yBase };
      });
    });

    return next;
  }, [activeNameSet, activePersons, activeRooms, clampInRoom, layoutPositionsInRoom]);

  // ===== stateMap & answerText (interactive only) =====
  const rectIntersectionArea = useCallback((a, b) => {
    const x1 = Math.max(a.x, b.x);
    const y1 = Math.max(a.y, b.y);
    const x2 = Math.min(a.x + a.w, b.x + b.w);
    const y2 = Math.min(a.y + a.h, b.y + b.h);
    const w = Math.max(0, x2 - x1);
    const h = Math.max(0, y2 - y1);
    return w * h;
  }, []);

  const getRoomByOverlap = useCallback((xBase, yBase) => {
    // ✅ Point detection: titik center-bottom pin (yang ditunjuk arrow)
    const pointX = xBase + pinBaseEff / 2;
    const pointY = yBase + pinBaseEff;

    for (const r of activeRooms) {
      if (
        pointX >= r.x &&
        pointX <= r.x + r.w &&
        pointY >= r.y &&
        pointY <= r.y + r.h
      ) {
        return r.code;
      }
    }
    return null;
  }, [activeRooms, pinBaseEff]);

  const stateMap = useMemo(() => {
    if (isExplanation) return {}; // explanation ga perlu stateMap interactive
    const m = {};
    activeCodes.forEach((c) => (m[c] = []));

    activePersons.forEach((p) => {
      const name = p.name;
      const pl = placements[name];
      if (!pl || pl.zone !== "floor") return;
      if (typeof pl.xBase !== "number" || typeof pl.yBase !== "number") return;

      const code = getRoomByOverlap(pl.xBase, pl.yBase);
      if (!code) return;
      if (!m[code]) m[code] = [];
      m[code].push(name);
    });
    activeCodes.forEach((c) => {
      m[c] = sortDragpinNames(m[c]);
    });
    return m;
  }, [activeCodes, activePersons, getRoomByOverlap, placements, isExplanation]);

  const answerText = useMemo(() => {
    if (isExplanation) return [];
    return activeCodes.map((code) => {
      const names = stateMap[code] || [];
      return names.length ? `${code}:${names.join(",")}` : `${code}:null`;
    });
  }, [activeCodes, stateMap, isExplanation]);

  // ===== Dispatch (FIX loop) =====
  const questionId = questionState?.id ?? questionState?.questionId ?? questionState?.question_id ?? null;
  const answerKey = useMemo(() => answerText.join("|"), [answerText]);

  const questionStateRef = useRef(questionState);
  useEffect(() => {
    questionStateRef.current = questionState;
  }, [questionState]);

  const dispatchTimerRef = useRef(null);
  useEffect(() => {
    if (isExplanation) return;
    if (!dispatch) return;
    if (!questionId) return;

    const current = coerceAnswerText(questionStateRef.current?.answerText);
    const currentKey = current.join("|");

    // ✅ kalau sama, jangan dispatch lagi (hindari loop)
    if (currentKey === answerKey) return;

    if (dispatchTimerRef.current) window.clearTimeout(dispatchTimerRef.current);
    dispatchTimerRef.current = window.setTimeout(() => {
      const latest = questionStateRef.current;
      if (!latest) return;

      dispatch({
        type: "question/setQuestion",
        payload: {
          ...latest,
          answerText,
        },
      });

      dispatchTimerRef.current = null;
    }, 250);

    return () => {
      if (dispatchTimerRef.current) window.clearTimeout(dispatchTimerRef.current);
    };
  }, [answerKey, answerText, dispatch, isExplanation, questionId]); // ✅ no questionState in deps

  // ===== Responsive: observe floor width =====
  useEffect(() => {
    const el = floorRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const w = entry.contentRect.width;
      if (!Number.isFinite(w) || w <= 0) return;

      const roundedW = Math.round(w);

      if (roRafRef.current) cancelAnimationFrame(roRafRef.current);
      roRafRef.current = requestAnimationFrame(() => {
        setFloorW((prev) => (Math.abs(prev - roundedW) < 1 ? prev : roundedW));
        roRafRef.current = null;
      });
    });

    ro.observe(el);
    return () => {
      ro.disconnect();
      if (roRafRef.current) cancelAnimationFrame(roRafRef.current);
    };
  }, []);

  // ===== Interactive hydrate/reset (skip when explanation) =====
  const lastQuestionIdRef = useRef(null);

  useEffect(() => {
    if (isExplanation) return; // ✅ skip di pembahasan
    if (questionId == null) return;

    if (lastQuestionIdRef.current !== questionId) {
      lastQuestionIdRef.current = questionId;
      hasInteractedRef.current = false;

      const m = {};
      activePersons.forEach((p) => (m[p.name] = { zone: "pool" }));
      setPlacements(m);
    }
  }, [isExplanation, activePersons, questionId]);

  useEffect(() => {
    setPlacements((prev) => {
      const next = {};
      activePersons.forEach((p) => {
        next[p.name] = prev[p.name] ?? { zone: "pool" };
      });
      return next;
    });
  }, [activePersons, activePersonsKey]);


  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (isExplanation) return;
    hasHydratedRef.current = false;
  }, [isExplanation, questionId]);

  useEffect(() => {
    if (isExplanation) return; // ✅ skip di pembahasan
    if (hasInteractedRef.current) return;
    if (hasHydratedRef.current) return;

    const incoming = coerceAnswerText(questionState?.answerText);
    if (!incoming.length) return;

    // pakai offset 0 untuk interactive hydrate
    setPlacements(buildPlacementsFromAnswer(incoming));
    hasHydratedRef.current = true;
  }, [isExplanation, questionId, activeRooms, questionState?.answerText, buildPlacementsFromAnswer]);

  // ===== Explanation placements (USER + KEY) =====
  // ✅ Untuk mode explanation, semua pins di pool (jangan di room)

  const explainUserPlacements = useMemo(() => {
    if (!isExplanation) return null;
    const placements = {};
    activePersons.forEach((p) => (placements[p.name] = { zone: "pool" }));
    return placements;
  }, [isExplanation, activePersons]);

  const explainKeyPlacements = useMemo(() => {
    if (!isExplanation) return null;
    const placements = {};
    activePersons.forEach((p) => (placements[p.name] = { zone: "pool" }));
    return placements;
  }, [isExplanation, activePersons]);

  // ===== Pointer drag handlers (interactive) =====
  const getRect = useCallback((el) => (el ? el.getBoundingClientRect() : null), []);

  const scheduleOverlayMove = () => {
    if (rafMoveRef.current) return;

    rafMoveRef.current = window.requestAnimationFrame(() => {
      rafMoveRef.current = null;
      const el = overlayRef.current;
      if (!el) return;
      const { x, y } = dragPosRef.current;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });
  };

  const setDraggingFromPointer = useCallback((clientX, clientY) => {
    const rootRect = getRect(rootRef.current);
    if (!rootRect) return;

    const nextX = clientX - rootRect.left - dragOffsetRef.current.ox;
    const nextY = clientY - rootRect.top - dragOffsetRef.current.oy;

    dragPosRef.current = { x: nextX, y: nextY };
    scheduleOverlayMove();

    // ✅ Deteksi room untuk tooltip real-time
    const floorRect = getRect(floorRef.current);
    if (floorRect) {
      const relX = nextX - (floorRect.left - rootRect.left);
      const relY = nextY - (floorRect.top - rootRect.top);
      const xBase = relX / scale;
      const yBase = relY / scale;
      const detectedRoom = getRoomByOverlap(xBase, yBase);
      setDraggingDetectedRoom(detectedRoom);
    }

    const poolRect = getRect(poolRef.current);
    if (!poolRect) return;

    const nextHover =
      clientX >= poolRect.left && clientX <= poolRect.right && clientY >= poolRect.top && clientY <= poolRect.bottom;

    setPoolHover((prev) => (prev === nextHover ? prev : nextHover));
  }, [getRect, getRoomByOverlap, scale]);

  const placementsRef = useRef(placements);
  useEffect(() => {
    placementsRef.current = placements;
  }, [placements]);

  useEffect(() => {
    if (isExplanation) return;

    const onMove = (e) => {
      const cand = dragCandidateRef.current;
      if (!cand || cand.pointerId !== e.pointerId) return;

      const dx = e.clientX - cand.startX;
      const dy = e.clientY - cand.startY;

      // ✅ belum mulai drag sungguhan → cek threshold
      if (dragPointerIdRef.current == null) {
        const dist = Math.hypot(dx, dy);
        if (dist < DRAG_THRESHOLD) return;

        // ✅ start drag sekarang
        dragPointerIdRef.current = cand.pointerId;
        dragOffsetRef.current = { ox: cand.ox, oy: cand.oy };

        const currentZone = placementsRef.current[cand.name]?.zone ?? "pool";
        dragFromZoneRef.current = currentZone;

        dragNameRef.current = cand.name;
        setDraggingName(cand.name);
        // setelah setDraggingName(cand.name)
        setDraggingFromPointer(e.clientX, e.clientY);
      }

      // ✅ kalau drag sudah aktif, gerakkan overlay
      if (dragPointerIdRef.current === e.pointerId) {
        setDraggingFromPointer(e.clientX, e.clientY);
      }
    };

    const onUp = (e) => {
      const activeId = dragPointerIdRef.current;
      const activeName = dragNameRef.current;

      // selalu bersihin kandidat
      dragCandidateRef.current = null;

      // ✅ kalau user cuma tap/long-press (tooltip) dan tidak drag → stop di sini
      if (activeId == null || activeId !== e.pointerId || !activeName) return;

      const floorRect = getRect(floorRef.current);
      const poolRect = getRect(poolRef.current);

      const pinLeft = e.clientX - dragOffsetRef.current.ox;
      const pinTop = e.clientY - dragOffsetRef.current.oy;
      const pinRight = pinLeft + pinSizePx;
      const pinBottom = pinTop + pinSizePx;

      const PAD = isDownSm ? 10 : 6;

      const droppedOnFloor =
        !!floorRect &&
        pinRight >= floorRect.left - PAD &&
        pinLeft <= floorRect.right + PAD &&
        pinBottom >= floorRect.top - PAD &&
        pinTop <= floorRect.bottom + PAD;

      const droppedOnPool =
        !!poolRect &&
        pinRight >= poolRect.left - PAD &&
        pinLeft <= poolRect.right + PAD &&
        pinBottom >= poolRect.top - PAD &&
        pinTop <= poolRect.bottom + PAD;

      const fromZone = dragFromZoneRef.current;

      setPlacements((prev) => {
        const next = { ...prev };

        if (droppedOnFloor && floorRect) {
          const xPx = pinLeft - floorRect.left;
          const yPx = pinTop - floorRect.top;

          const rawXBase = xPx / scale;
          const rawYBase = yPx / scale;

          const clamped = clampFloorPlacement(rawXBase, rawYBase);
          const detectedRoom = getRoomByOverlap(clamped.xBase, clamped.yBase);

          if (detectedRoom) {
            next[activeName] = { zone: "floor", xBase: clamped.xBase, yBase: clamped.yBase };
          } else {
            // ✅ di luar room → kembali ke pool
            next[activeName] = { zone: "pool" };
          }
        } else if (droppedOnPool) {
          next[activeName] = { zone: "pool" };
        } else {
          // ✅ Jika drop di luar box, selalu kembali ke pool
          next[activeName] = { zone: "pool" };
        }

        return next;
      });

      hasInteractedRef.current = true;

      if (rafMoveRef.current) {
        cancelAnimationFrame(rafMoveRef.current);
        rafMoveRef.current = null;
      }
      if (overlayRef.current) overlayRef.current.style.transform = "translate3d(0px, 0px, 0)";

      // reset drag state
      setDraggingName(null);
      setDraggingDetectedRoom(null);
      setPoolHover(false);
      dragPointerIdRef.current = null;
      dragNameRef.current = null;
    };

    const onCancel = () => {
      dragCandidateRef.current = null;

      if (rafMoveRef.current) {
        cancelAnimationFrame(rafMoveRef.current);
        rafMoveRef.current = null;
      }
      if (overlayRef.current) overlayRef.current.style.transform = "translate3d(0px, 0px, 0)";

      setDraggingName(null);
      setDraggingDetectedRoom(null);
      setPoolHover(false);
      dragPointerIdRef.current = null;
      dragNameRef.current = null;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [isExplanation, pinSizePx, scale, isDownSm, pinBaseEff, clampFloorPlacement, setDraggingFromPointer, getRect, getRoomByOverlap]);

  const startDrag = (name) => (e) => {
    if (isExplanation) return;
    e.preventDefault();

    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();

    // simpan kandidat drag, tapi JANGAN setDraggingName dulu
    dragCandidateRef.current = {
      name,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      ox: e.clientX - rect.left,
      oy: e.clientY - rect.top,
    };

    // boleh capture supaya move/up tetap ketangkep
    el.setPointerCapture(e.pointerId);
  };

  const handleReset = () => {
    if (isExplanation) return;

    const m = {};
    activePersons.forEach((p) => (m[p.name] = { zone: "pool" }));
    setPlacements(m);
    setCheckResult(null);

    hasInteractedRef.current = true;
  };

  // ===== Cek Jawaban =====
  const [checkResult, setCheckResult] = useState(null);

  const handleCheckAnswer = () => {
    if (isExplanation) return;

    // Parse kunci jawaban: "Room 1:Ika,Hafidh" → { "Room 1": ["Ika","Hafidh"] }
    const keyMap = {};
    const raw = coerceAnswerText(keyAnswerText);
    raw.forEach((row) => {
      const [code, rest] = String(row).split(":");
      if (!code) return;
      const names =
        !rest || rest === "null"
          ? []
          : rest.split(",").map((s) => s.trim()).filter(Boolean);
      keyMap[code] = names;
    });

    // Build current answer dari placements
    const currentMap = {};
    activeCodes.forEach((c) => (currentMap[c] = []));
    activePersons.forEach((p) => {
      const pl = placements[p.name];
      if (!pl || pl.zone !== "floor") return;
      if (typeof pl.xBase !== "number" || typeof pl.yBase !== "number") return;
      const code = getRoomByOverlap(pl.xBase, pl.yBase);
      if (code) currentMap[code].push(p.name);
    });

    // Compare per room (urutan nama di dalam room tidak berpengaruh)
    const perRoom = activeCodes.map((code) => {
      const userNames = sortDragpinNames(currentMap[code] || []);
      const keyNames = sortDragpinNames(keyMap[code] || []);
      const correct =
        userNames.length === keyNames.length &&
        userNames.every((n, i) => n === keyNames[i]);
      return { code, userNames, keyNames, correct };
    });

    const allCorrect = perRoom.every((r) => r.correct);
    setCheckResult({ perRoom, allCorrect });
  };

  // ===== Pin render =====
  // ✅ Revisi: pin sekarang text only dengan bentuk badge elips
  const renderPin = (
    p,
    personIndex, // ✅ index untuk custom display name
    tone = "user",
    styleOverride = {},
    disableTooltip = false
  ) => {
    const displayName = getDisplayNameForPin(personIndex);
    const isHidden = draggingName === p.name && !disableTooltip;
    const s = pinSizePx;

    const pinColor = tone === "key" ? themeColors.success : themeColors.primary;
    const textColor = themeColors.white;

    const pinStyle = {
      width: s * 2.6,
      height: s * 0.7,
      position: "relative",
      zIndex: tone === "key" ? 4 : 6,
      opacity: isHidden ? 0 : tone === "key" ? 0.92 : 1,
      pointerEvents: isHidden ? "none" : "auto",
      cursor: isExplanation ? "default" : "grab",
      userSelect: "none",
      touchAction: "none",
      overflow: "visible",
      ...styleOverride,
    };

    const PinRoot = (
      <div
        key={`pin-${p.name}`}
        data-person={p.name}
        onPointerDown={isExplanation ? undefined : startDrag(p.name)}
        style={pinStyle}
        onMouseDown={(e) => e.currentTarget.style.cursor = isExplanation ? "default" : "grabbing"}
        onMouseUp={(e) => e.currentTarget.style.cursor = isExplanation ? "default" : "grab"}
      >
        {/* badge elips */}
        <div
          style={{
            width: "100%",
            height: "100%",
            background: pinColor,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: s * 0.15,
            paddingRight: s * 0.15,
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
            border: tone === "key" ? `2px solid ${themeColors.success}` : `2px solid ${pinColor}`,
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              fontSize: `clamp(10px, ${s * 0.28}px, 14px)`,
              fontWeight: 700,
              color: textColor,
              lineHeight: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </span>
        </div>
      </div>
    );

    return PinRoot;
  };

  // ===== Render helpers for floor pins =====
  const renderInteractiveFloorPins = () => (
    <div style={{ position: "absolute", inset: 0, zIndex: 5 }}>
      {activePersons.map((p, idx) => {
        const pl = placements[p.name];
        if (!pl || pl.zone !== "floor") return null;
        if (typeof pl.xBase !== "number" || typeof pl.yBase !== "number") return null;

        return (
          <div
            key={`floor-${p.name}`}
            style={{ position: "absolute", left: pl.xBase * scale, top: pl.yBase * scale }}
          >
            {renderPin(p, idx, "user")}
          </div>
        );
      })}
    </div>
  );

  const renderExplanationFloorPins = () => {
    const userPl = explainUserPlacements;
    const keyPl = explainKeyPlacements;

    return (
      <div style={{ position: "absolute", inset: 0, zIndex: 5 }}>
        {/* KEY (hijau) */}
        {activePersons.map((p, idx) => {
          const pl = keyPl?.[p.name];
          if (!pl || pl.zone !== "floor") return null;
          if (typeof pl.xBase !== "number" || typeof pl.yBase !== "number") return null;

          return (
            <div
              key={`key-${p.name}`}
              style={{ position: "absolute", left: pl.xBase * scale, top: pl.yBase * scale }}
            >
              {renderPin(p, idx, "key")}
            </div>
          );
        })}

        {/* USER (biru) */}
        {activePersons.map((p, idx) => {
          const pl = userPl?.[p.name];
          if (!pl || pl.zone !== "floor") return null;
          if (typeof pl.xBase !== "number" || typeof pl.yBase !== "number") return null;

          return (
            <div
              key={`user-${p.name}`}
              style={{ position: "absolute", left: pl.xBase * scale, top: pl.yBase * scale }}
            >
              {renderPin(p, idx, "user")}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      className={isExplanation ? "chart-col-full" : "chart-col-full interactive"}
      style={{ width: "100%", position: "relative", background: "transparent", display: "flex", alignItems: "stretch", justifyContent: "flex-start" }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", minWidth: 0 }}>
        {/* ✅ Pin Information Table — tampilkan deskripsi karakteristik pin */}
        {Array.isArray(dragpinPins) && dragpinPins.length > 0 && (
          <div style={{ width: "100%", boxSizing: "border-box" }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: themeColors.black }}>
              Deskripsi Karakteristik Pin
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {dragpinPins.map((pin, idx) => {
                const displayName =
                  presetPinCustomNames && presetPinCustomNames[idx]
                    ? presetPinCustomNames[idx]
                    : `Pin ${idx + 1}`;
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      padding: "8px 10px",
                      background: "rgba(0,0,0,0.02)",
                      borderRadius: 8,
                      border: `1px solid ${themeColors.stroke}`,
                    }}
                  >
                    {/* Badge nama pin */}
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 80,
                        height: 30,
                        padding: "0 10px",
                        background: themeColors.primary,
                        color: themeColors.white,
                        borderRadius: 999,
                        fontWeight: 700,
                        fontSize: 11,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flexShrink: 0,
                      }}
                    >
                      {displayName}
                    </div>
                    {/* Label/deskripsi pin — render HTML */}
                    <div
                      style={{ flex: 1, minWidth: 0, fontSize: 13, color: themeColors.secondary }}
                      dangerouslySetInnerHTML={{ __html: pin.label || "(Tidak ada deskripsi)" }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* FLOOR */}
        <div
          ref={floorRef}
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "100%",
            aspectRatio: `${BASE_W} / ${BASE_H}`,
            borderRadius: isDownSm ? "8px" : "12px",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* rooms-layer */}
          <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
            {activeRooms.map((r, roomIdx) => {
              const bgUrl = r.bgKey ? images[r.bgKey] : undefined;
              const roomDisplayName = getDisplayNameForRoom(roomIdx);

              return (
                <div
                  key={r.code}
                  data-code={r.code}
                  style={{
                    position: "absolute",
                    left: r.x * scale,
                    top: r.y * scale,
                    width: r.w * scale,
                    height: r.h * scale,
                    border: `${isDownSm ? 1 : 2}px solid ${themeColors.black}`,
                    borderRadius: isDownSm ? "8px" : "10px",
                    fontWeight: 800,
                    userSelect: "none",
                    pointerEvents: "none",
                    background: bgUrl ? `url("${bgUrl}") center / cover no-repeat` : getRoomColor(roomIdx),
                    color: themeColors.black,
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: isDownSm ? "8px" : "10px",
                      background: bgUrl ? "rgba(255,255,255,0.08)" : "transparent",
                    }}
                  />

                  <div style={{ position: "relative", zIndex: 1, width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 0.6 * scale, paddingRight: 0.6 * scale, minHeight: 26 * scale, boxSizing: "border-box" }}>
                      <span
                        style={{
                          fontSize: `clamp(10px, ${12 * scale}px, 13px)`,
                          fontWeight: 900,
                          paddingLeft: isDownSm ? "5.6px" : "7.2px",
                          paddingRight: isDownSm ? "5.6px" : "7.2px",
                          paddingTop: "2px",
                          paddingBottom: "2px",
                          border: `${isDownSm ? 1 : 2}px solid ${themeColors.black}`,
                          borderRadius: "999px",
                          background: themeColors.white,
                          lineHeight: 1.2,
                          display: "inline-block",
                        }}
                      >
                        {roomDisplayName}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* pins */}
          {isExplanation ? renderExplanationFloorPins() : renderInteractiveFloorPins()}
        </div>

        {/* ✅ Reset + Cek Jawaban + Pool hanya saat interactive */}
        {!isExplanation ? (
          <>
            <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={handleCheckAnswer}
                style={{
                  fontWeight: 900,
                  borderRadius: "999px",
                  border: `1px solid ${themeColors.success}`,
                  color: themeColors.white,
                  background: themeColors.success,
                  paddingLeft: isDownSm ? "12.8px" : "16px",
                  paddingRight: isDownSm ? "12.8px" : "16px",
                  paddingTop: isDownSm ? "4.4px" : "5.6px",
                  paddingBottom: isDownSm ? "4.4px" : "5.6px",
                  cursor: "pointer",
                  fontSize: isDownSm ? "13px" : "14px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#0a8a85";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = themeColors.success;
                }}
              >
                Cek Jawaban
              </button>
              <button
                onClick={handleReset}
                style={{
                  fontWeight: 900,
                  borderRadius: "999px",
                  border: `1px solid ${themeColors.stroke}`,
                  color: themeColors.black,
                  background: themeColors.white,
                  paddingLeft: isDownSm ? "12.8px" : "16px",
                  paddingRight: isDownSm ? "12.8px" : "16px",
                  paddingTop: isDownSm ? "4.4px" : "5.6px",
                  paddingBottom: isDownSm ? "4.4px" : "5.6px",
                  cursor: "pointer",
                  fontSize: isDownSm ? "13px" : "14px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = themeColors.black;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = themeColors.stroke;
                }}
              >
                Reset
              </button>
            </div>

            {/* ✅ Hasil cek jawaban */}
            {checkResult && (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: checkResult.allCorrect ? "#e6f9f4" : "#fff0f0",
                  border: `2px solid ${checkResult.allCorrect ? themeColors.success : themeColors.error}`,
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 14,
                    color: checkResult.allCorrect ? themeColors.success : themeColors.error,
                    marginBottom: 6,
                  }}
                >
                  {checkResult.allCorrect
                    ? "✅ Semua jawaban benar!"
                    : "❌ Jawaban belum tepat, cek per room:"}
                </div>
                {checkResult.perRoom.map((r) => {
                    const roomIdx = activeCodes.indexOf(r.code);
                    const roomLabel = roomIdx >= 0 ? getDisplayNameForRoom(roomIdx) : r.code;
                    return (
                    <div
                      key={r.code}
                      style={{
                        fontSize: 12,
                        marginBottom: 2,
                        color: r.correct ? themeColors.success : themeColors.error,
                      }}
                    >
                      <b>{roomLabel}:</b>{" "}
                      {r.correct
                        ? `✅ ${r.userNames.join(", ") || "(kosong)"}`
                        : `❌ [${r.userNames.join(", ") || "(kosong)"}] → seharusnya [${r.keyNames.join(", ") || "(kosong)"}]`}
                    </div>
                    );
                  })}
              </div>
            )}

            <div
              ref={poolRef}
              style={{
                border: `2px dashed ${themeColors.stroke}`,
                borderRadius: "4px",
                padding: isDownSm ? "8px" : "12px",
                background: "#fafafa",
                outline: poolHover ? `3px solid ${themeColors.black}` : "none",
                outlineOffset: poolHover ? "2px" : "0px",
                transition: "outline 140ms ease",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isDownSm ? "repeat(auto-fit, minmax(130px, 1fr))" : "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: isDownSm ? "8.8px" : "12px",
                  alignItems: "start",
                  justifyItems: "center",
                }}
              >
                {activePersons
                  .filter((p) => (placements[p.name]?.zone ?? "pool") === "pool")
                  .map((p) => {
                    // ✅ get index asli dari activePersons, bukan index setelah filter
                    const originalIdx = activePersons.findIndex((x) => x.name === p.name);
                    return renderPin(p, originalIdx, "user");
                  })}
              </div>
            </div>

            {/* Drag overlay */}
            {draggingName ? (
              <div ref={overlayRef} style={{ position: "absolute", left: 0, top: 0, zIndex: 20, pointerEvents: "none" }}>
                {(() => {
                  const draggedPerson = activePersons.find((x) => x.name === draggingName) || { name: draggingName };
                  const draggedIdx = activePersons.findIndex((x) => x.name === draggingName);
                  const showArrow = !!draggingDetectedRoom;

                  return (
                    <div style={{ position: "relative" }}>
                      {/* Room label badge */}
                      {showArrow && draggingDetectedRoom && (
                        <div style={{
                          position: "absolute",
                          top: -28,
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: themeColors.success,
                          color: themeColors.white,
                          paddingLeft: 8,
                          paddingRight: 8,
                          paddingTop: 3,
                          paddingBottom: 3,
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 800,
                          whiteSpace: "nowrap",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                          zIndex: 9,
                        }}>
                          {draggingDetectedRoom}
                        </div>
                      )}

                      {renderPin(draggedPerson, draggedIdx, "user", {}, true)}

                      {/* Arrow pointing down */}
                      {showArrow && draggingDetectedRoom && (
                        <div style={{
                          position: "absolute",
                          bottom: -14,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 0,
                          height: 0,
                          borderLeft: "6px solid transparent",
                          borderRight: "6px solid transparent",
                          borderTop: `9px solid ${themeColors.success}`,
                          filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.3))",
                        }} />
                      )}

                      {/* Dot indicator - titik deteksi */}
                      {showArrow && draggingDetectedRoom && (
                        <div style={{
                          position: "absolute",
                          bottom: 0,
                          left: "50%",
                          transform: "translate(-50%, 50%)",
                          width: 8,
                          height: 8,
                          background: themeColors.success,
                          borderRadius: "50%",
                          border: `2px solid ${themeColors.white}`,
                          boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          zIndex: 8,
                        }} />
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : null}
          </>
        ) : (
          // ✅ Explanation mode: show pool pins dengan key (hijau) + user (biru)
          <div
            ref={poolRef}
            style={{
              border: `2px dashed ${themeColors.stroke}`,
              borderRadius: "4px",
              padding: isDownSm ? "8px" : "12px",
              background: "#fafafa",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isDownSm ? "repeat(auto-fit, minmax(130px, 1fr))" : "repeat(auto-fit, minmax(140px, 1fr))",
                gap: isDownSm ? "8.8px" : "12px",
                alignItems: "start",
                justifyItems: "center",
              }}
            >
              {/* USER (biru) */}
              {activePersons.map((p, idx) => {
                const userPl = explainUserPlacements?.[p.name];
                if (!userPl || userPl.zone !== "pool") return null;
                return renderPin(p, idx, "user");
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}